import { useMemo } from "react";
import {
  hasDuplicates,
  isIncreasing,
  nonNegative,
} from "../../utils/array.functions";

/**
 * Customized hook to validate Activation Profiles
 * @param {Array} activationProfiles - Activation Profiles Array
 * @returns {Object} - Object with validations, errors, and alerts
 */
export const useActivationProfilesValidation = (activationProfiles) => {
  const filterNonEmptyValues = (arr) =>
    arr.filter(
      (value) => value !== undefined && value !== null && value !== "",
    );

  const validationResults = useMemo(() => {
    const valuesArr = filterNonEmptyValues(
      activationProfiles?.map((item) => item.siteActivationProfilePct) || [],
    );
    const daysArr = filterNonEmptyValues(
      activationProfiles?.map((item) => item.siteActivationProfileDays) || [],
    );

    const isValueIncreasing = isIncreasing(valuesArr);
    const valuesHasDuplicates = hasDuplicates(valuesArr);
    const isDaysIncreasing = isIncreasing(daysArr);
    const isDaysNonNegative = nonNegative(daysArr);
    const daysHasDuplicates = hasDuplicates(daysArr);

    return {
      isValueIncreasing,
      valuesHasDuplicates,
      isDaysIncreasing,
      isDaysNonNegative,
      daysHasDuplicates,
    };
  }, [activationProfiles]);

  const errorsArray = useMemo(() => {
    const errors = [];
    if (!validationResults.isValueIncreasing) errors.push("valueIncreasing");
    if (validationResults.valuesHasDuplicates) errors.push("valuesHasDuplicates");
    if (!validationResults.isDaysIncreasing) errors.push("daysIncreasing");
    if (!validationResults.isDaysNonNegative) errors.push("daysNonNegative");
    if (validationResults.daysHasDuplicates) errors.push("daysHasDuplicates");
    return errors;
  }, [validationResults]);

  const alerts = useMemo(() => [
    {
      condition: !validationResults.isValueIncreasing,
      message: "Number of sites should be increasing.",
    },
    {
      condition: validationResults.valuesHasDuplicates,
      message: "Number of sites should not have duplicates.",
    },
    { 
      condition: !validationResults.isDaysIncreasing, 
      message: "Days should be increasing." 
    },
    { 
      condition: !validationResults.isDaysNonNegative, 
      message: "Days should be non-negative." 
    },
    { 
      condition: validationResults.daysHasDuplicates, 
      message: "Days should not have duplicates." 
    },
  ], [validationResults]);

  return {
    validations: validationResults,
    errorsArray,
    alerts,
  };
};

/**
 * Utility function to validate activation profiles from Excel data
 * @param {Array} activationProfilesData - Raw activation profiles data from Excel
 * @param {Array} countryNames - Array of country names
 * @param {Object} studyPlan - Study plan object (optional for overall validation)
 * @param {String} studyPlanType - Study plan type (optional for overall validation)
 * @returns {Object} - Object with hasErrors boolean and errorMessages array
 */
export const validateActivationProfilesTable = (activationProfilesData) => {
  const CARD_PERCENTAGES = [
    'CARD_1_PERCENT', 'CARD_2_PERCENT', 'CARD_3_PERCENT', 'CARD_4_PERCENT', 
    'CARD_5_PERCENT', 'CARD_6_PERCENT', 'CARD_7_PERCENT', 'CARD_8_PERCENT', 
    'CARD_9_PERCENT', 'CARD_10_PERCENT', 'CARD_11_PERCENT'
  ];

  const filterNonEmptyValues = (arr) =>
    arr.filter(
      (value) => value !== undefined && value !== null && value !== "",
    );

  const validateSingleProfile = (profileData) => {
    const valuesArr = filterNonEmptyValues(profileData.siteActivationProfilePct || []);
    const daysArr = filterNonEmptyValues(profileData.siteActivationProfileDays || []);

    const errors = [];
    const messages = [];

    // Validate percentage values
    if (!isIncreasing(valuesArr)) {
      errors.push("valueIncreasing");
      messages.push("Number of sites should be increasing.");
    }
    if (hasDuplicates(valuesArr)) {
      errors.push("valuesHasDuplicates");
      messages.push("Number of sites should not have duplicates.");
    }
    if (!nonNegative(valuesArr)) {
      errors.push("sitesNonNegative");
      messages.push("Number of sites should be non-negative.");
    }

    // Validate days values
    if (!isIncreasing(daysArr)) {
      errors.push("daysIncreasing");
      messages.push("Days should be increasing.");
    }
    if (!nonNegative(daysArr)) {
      errors.push("daysNonNegative");
      messages.push("Days should be non-negative.");
    }
    if (hasDuplicates(daysArr)) {
      errors.push("daysHasDuplicates");
      messages.push("Days should not have duplicates.");
    }

    return { errors, messages };
  };

  // Transform activation profiles data for validation
  const activationProfilesForValidation = activationProfilesData
    .filter(profile => profile.overrideFl === 1)
    .map(profile => {
      // Extract percentage values (CARD_X_PERCENT)
      const percentageValues = CARD_PERCENTAGES
        .map(cardKey => profile[cardKey])
        .filter(value => value !== null && value !== undefined && value !== "");
      
      // Extract day values (CARD_X_DATE) - convert to numeric days if needed
      const dayValues = CARD_PERCENTAGES
        .map(cardKey => {
          const dateKey = cardKey.replace('_PERCENT', '_DATE');
          const dateValue = profile[dateKey];
          
          if (dateValue !== null && dateValue !== undefined && dateValue !== "") {
            // If it's a number, return it directly
            if (typeof dateValue === 'number') {
              return dateValue;
            }
            // If it's a string, try to parse as number
            const numValue = Number(dateValue);
            if (!isNaN(numValue)) {
              return numValue;
            }
            // For date strings, we'll create sequential numeric values
            const index = CARD_PERCENTAGES.indexOf(cardKey);
            return index * 30; // Assume 30 days between cards as default
          }
          return null;
        })
        .filter(value => value !== null && value !== undefined && value !== "");

      return {
        countryName: profile.countryName,
        siteActivationProfilePct: percentageValues,
        siteActivationProfileDays: dayValues
      };
    });

  // Validate activation profiles for each country
  let hasValidationErrors = false;
  let validationErrorMessages = [];

  for (const profileData of activationProfilesForValidation) {
    // Validate this specific country's profile
    const countryValidation = validateSingleProfile(profileData);
    
    if (countryValidation.errors.length > 0) {
      hasValidationErrors = true;
      validationErrorMessages.push(`${profileData.countryName}: ${countryValidation.messages.join(', ')}`);
    }
  }

  return {
    hasErrors: hasValidationErrors,
    errorMessages: validationErrorMessages,
  };
};
